/**
 * PATH: packages/ui/src/kanban/components/KanbanCard.tsx
 * FILE_ID: UI-KANBAN-CARD-001
 * PURPOSE: Thin card orchestrator — hooks, lock/hover chrome, then
 *          KanbanCardFace for the operator-ordered blocks.
 * RELATED: ./KanbanCardFace.tsx; ./board-view/cardFacePrefs.ts.
 */
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { useIsCoarsePointer } from '../useIsCoarsePointer'
import { useIsPhone } from '../useIsPhone'
import { getCardAllocationId, isCardAiPending } from '../allocation-source'
import { resolveCardColorStyle } from '../cardColors'
import { buildKanbanCardClassName } from './kanbanCardClassName'
import { cardAccentBorder, cardElevation, cardPriorityBorder, cardRing, composeCardBoxShadow } from '../surfaceTokens'
import { todayLocalYmd } from '../utils/localDate'
import { composeCardDetailContributions } from '../card-surface/details/CardDetailContributions'
import {
  isCardFaceBlockVisible,
  resolveCardFacePrefs,
  useKanbanCardFacePrefs,
} from './board-view/cardFacePrefs'
import {
  applyCardFaceExtras,
  applyCardFaceRail,
  applyCardFaceStrip,
} from './board-view/kanbanCardFaceChrome'
import { railShows } from './board-view/cardFaceRecipe'
import { resolveCardTileHeight, storedCardTileBody } from './board-view/cardTileHeight'
import { KanbanCardAllocateHotkey } from './KanbanCardAllocateHotkey'
import { KanbanCardFace } from './KanbanCardFace'
import { buildKanbanCardFaceSlots } from './KanbanCardFaceSlots'
import { CardAiBand } from './CardAiBand'
import { KanbanCardDetailSlot } from './KanbanCardDetailSlot'
import { KanbanCardHoverMenu } from './KanbanCardHoverMenu'
import { KanbanCardInlineChecklist } from './KanbanCardInlineChecklist'
import { KanbanCardLockedFace } from './KanbanCardLockedFace'
import { KanbanCardOverlays } from './KanbanCardOverlays'
import { getKanbanCardThreadSupport } from './kanbanCardThreadSupport'
import { pickCardAccent } from './kanbanCardVisuals'
import { useKanbanCardInteractions } from './useKanbanCardInteractions'
import { useKanbanCardPresentation } from './useKanbanCardPresentation'
import type { KanbanCardProps } from './KanbanCard.types'

export type { KanbanCardProps } from './KanbanCard.types'

/** Field ids gated on the card root (cover, priority edge, due, AI lane). */
export const CARD_FACE_ROOT_FIELD_IDS = [
  'due',
  'colorCover',
  'priority',
  'suggestedLane',
] as const

// PERF-2026-06-22-KANBAN-CARD-MEMO — memoised so re-rendering one lane (or the
// board) doesn't re-render every card. KanbanLane passes only stable refs
// (handlers via the parent's memoised callback bundles, `onDragStart` via a
// useCallback) plus per-card primitives + the stable `card` object, so a
// shallow prop compare is correct: a card re-renders only when its own data /
// selection / find-hit state changes.
export const KanbanCard = memo(function KanbanCard({
  card,
  onOpen,
  onContextMenu,
  draggable = true,
  onDragStart,
  onDropAsChild,
  onDragEnd,
  detailsSlot,
  resolveMember,
  onRenameInline,
  // Prop default stays 'expanded' even though the board view's
  // localStorage default is now 'compact' (qf!Newmark-board-density).
  // The board path passes density explicitly via `cardDensity`, so
  // operators see compact in production regardless of this default.
  // Keeping 'expanded' here preserves direct-callers (storybook,
  // unit tests for AI band / context menu / longPress) that assume
  // the rich face.
  density = 'expanded',
  resolveEmailMeta,
  onEmailAllocate,
  onOpenEmail,
  api,
  onAiAllocationChanged,
  notify,
  onChecklistChanged,
  onToggleComplete,
  keyboardMode = false,
  highlightQuery,
  isActiveFindHit = false,
  selected = false,
  onSelectToggle,
  checklistForceOpen = false,
  quickActions,
  laneKind,
  relatedCount = 0,
  unseenCount = 0,
  currentUserId,
  cardSurfaceId = 'board-lane-card',
  cardSurfaceMode,
  boardId,
  boardKind,
}: KanbanCardProps) {
  const [cardFacePrefs] = useKanbanCardFacePrefs(currentUserId)
  const resolvedFace = resolveCardFacePrefs(cardFacePrefs, { boardId, boardKind })
  const showDue = resolvedFace.fields.due
  const { assignedMembers, emailMeta } = useKanbanCardPresentation(
    card,
    resolveMember,
    resolveEmailMeta,
  )
  const checklistDone = card.checklist_summary?.done ?? 0
  const checklistTotal = card.checklist_summary?.total ?? 0
  const commentCount = card.comments_count ?? 0
  const accent = useMemo(() => pickCardAccent(card), [card])
  // BUG-2026-06-24-KANBAN-CARD-MOBILE-BLANK-ROW — on phones the "⋯ more" action
  // moves OUT of a dedicated zone row (which left a tall blank strip with a
  // lone right-floated pill) and INTO the footer's right edge, riding the
  // existing meta line. Desktop keeps the hover-grow action zone untouched.
  const isPhone = useIsPhone()
  const isCoarsePointer = useIsCoarsePointer()
  // CRM-QUOTE-PIPELINE — crm-quote cards (fed from report.Quote) get a quote
  // footer (value / win% / age-in-lane / chase pip + CRM deep-link) in place
  // of the generic tenant detail slot. Gated on the feeder's provenance stamp
  // so no other card pays for it.
  const isQuoteCard = card.external_provider === 'crm-quote'
  const hasChecklistItems = checklistTotal > 0
  const checklistInlineReady = !!api && !!onChecklistChanged
  // FEAT-2026-06-20-KANBAN-CARD-AI-THREAD (FE stage 3) — promote an AI action
  // item from the thread peek into a real checklist step. Only wired when the
  // host supplies both the checklist write API and the change hook.
  const peekChecklistReady =
    !!api?.listChecklists &&
    !!api?.createChecklist &&
    !!api?.createChecklistItem &&
    !!onChecklistChanged

  // Inline-checklist panel state. False on first render so the lazy fetch only
  // fires on the operator's first expand (chip or hover dwell).
  const [checklistExpanded, setChecklistExpanded] = useState(false)
  // Bumped after a peek-driven checklist add so the inline expand-down panel
  // (if already open) re-fetches and shows the new step without a reopen.
  const [checklistRefreshTick, setChecklistRefreshTick] = useState(0)
  const handleAddChecklistStep = useCallback(
    async (text: string) => {
      const title = text.trim()
      if (!title || !api) return
      const lists = await api.listChecklists(card.id)
      const checklistId =
        lists?.[0]?.id ?? (await api.createChecklist(card.id, 'Checklist')).id
      await api.createChecklistItem({ checklist: checklistId, title })
      onChecklistChanged?.(card)
      setChecklistRefreshTick((t) => t + 1)
    },
    [api, card, onChecklistChanged],
  )

  const {
    hovered,
    chromeArmed,
    touchChromeOpen,
    dragging,
    emailDropHover,
    nestDropHover,
    justAllocated,
    hoverExpand,
    phoneChromeOpen,
    togglePhoneChrome,
    rootHandlers,
  } = useKanbanCardInteractions({
    card,
    onOpen,
    onSelectToggle,
    onContextMenu,
    onEmailAllocate,
    onDropAsChild,
    onDragStart,
    onDragEnd,
    checklistInlineReady,
    hasChecklistItems,
  })

  // BUG-2026-07-29-KANBAN-HOVER-STRIP-OVER-PICKER — while a bottom-rail
  // picker is open, keep chrome mounted (pointer may leave the card for
  // the portaled menu) and suspend the right-edge hover strip so paint /
  // calculator icons cannot stack above the active popup.
  const [quickPickerOpen, setQuickPickerOpen] = useState(false)
  const [allocateHotkey, setAllocateHotkey] = useState(false)
  useEffect(() => {
    const onOpen = (event: Event) => {
      const cardId = (event as CustomEvent<{ cardId?: string }>).detail?.cardId
      if (cardId === card.id) setAllocateHotkey(true)
    }
    window.addEventListener('kanban:open-card-allocate', onOpen)
    return () => window.removeEventListener('kanban:open-card-allocate', onOpen)
  }, [card.id])

  const tileHeight = resolveCardTileHeight({
    tileBody: storedCardTileBody(cardFacePrefs, boardId),
    density,
    pointerOnCard: hovered || phoneChromeOpen || touchChromeOpen,
  })
  const isCompact = tileHeight.isCompact
  const isLarge = tileHeight.isLarge
  const shownDensity = tileHeight.shownDensity
  const checklistApiReady = !!api && !!onChecklistChanged && !isCompact

  // FEAT-2026-07-24-KANBAN-HOVER-CHROME-DWELL — chrome (lift / action zone /
  // strip / note) waits for chromeArmed (~400ms dwell, scroll-suppressed).
  // Immediate `hovered` still drives subtle footer cues; hotkeys use the
  // board-level data-card-id path and do not need chromeArmed.
  // FEAT-2026-08-03-KANBAN-TABLET-LONGPRESS-RAIL — `touchChromeOpen` is the
  // tablet's way in: a hold arms the SAME chrome a mouse arms by dwelling, so
  // both the bottom rail and the right-edge strip appear exactly as on desktop
  // rather than through a second, touch-only toolbar.
  const extras = applyCardFaceExtras(resolvedFace)
  // Tile height is independent of the rail. Expanded stays tall.
  // The rail still follows rail mode (hover / always / hidden).
  const armed =
    resolvedFace.railMode === 'always' ||
    resolvedFace.tileOpen === 'expand'
      ? true
      : chromeArmed || touchChromeOpen
  const hoverActive =
    resolvedFace.railMode !== 'hidden' &&
    ((isPhone ? phoneChromeOpen : armed) || quickPickerOpen || allocateHotkey) &&
    !dragging &&
    !emailDropHover &&
    !nestDropHover
  const threadSupport = extras.showThread
    ? getKanbanCardThreadSupport(card, api)
    : { hasAiThread: false, canShowThread: false }
  const effectiveChecklistExpanded = extras.showChecklistExpand
    ? checklistExpanded || hoverExpand || checklistForceOpen
    : checklistExpanded || checklistForceOpen

  // Local-calendar compare (UAT P0 timezone sweep): due TODAY is not overdue.
  const overdue =
    card.due_date != null &&
    card.status !== 'DONE' &&
    card.status !== 'ARCHIVED' &&
    card.due_date.slice(0, 10) < todayLocalYmd()
  const lowConfidence = card.confidence != null && card.confidence < 0.6
  // EK-09 (display half): surface the numeric AI confidence.
  //   confidence == null → no pill;  >= 0.6 → grey "AI 87%";  < 0.6 → amber.
  const aiConfidencePct = card.confidence != null ? Math.round(card.confidence * 100) : null

  // Card surface (Stage 2 of the visual lift). Surface literals live in
  // ../surfaceTokens; the accent is a real `border-left`, so box-shadow carries
  // only the active-state ring + a single soft elevation.
  // Elevation / hover ring follow chromeArmed (via hoverActive) so skim
  // passes don't bounce every card under the cursor.
  const cardElevationShadow = cardElevation({
    dragging,
    hovered: hoverActive,
    emailDropHover,
    nestDropHover,
  })
  const cardRingShadow = cardRing({
    isActiveFindHit,
    selected,
    emailDropHover,
    justAllocated,
    nestDropHover,
    dragging,
    hovered: hoverActive,
    blocked: card.status === 'BLOCKED',
  })
  const cardBoxShadow = composeCardBoxShadow(cardRingShadow, cardElevationShadow)
  const accentBorder = accent ? cardAccentBorder(accent) : undefined
  // FEAT-2026-06-29-KANBAN-CARD-PRIORITY-EDGE — paint a subtle left bar for
  // High/Urgent so priority is visible on the resting tile (not just the
  // hover-peek / drawer). `undefined` falls through to the themed 1px border.
  const priorityBorder = cardPriorityBorder(card.priority)
  // FEAT-2026-06-22-KANBAN-CARD-COLOR — the operator-chosen tile colour resolves
  // to a soft, mode-aware tint composited over `--qc-card-surface`. When unset,
  // `background` is undefined and the card keeps its themed `--card-bg` default.
  const colorStyle = useMemo(() => resolveCardColorStyle(card.color), [card.color])

  // BUG-2026-06-20-KANBAN-CARD-EMPTY-DETAIL-STRIP — render the tenant detail
  // slot up front so the divider + padding wrapper is suppressed when it
  // produces nothing. The Time view's `composedSlot` (BoardGanttBucketView) is
  // ALWAYS a function but returns `undefined` for Overdue / Unscheduled buckets
  // (and on any host that wires no underlying `cardDetailsSlot`, e.g. the main
  // hub). Previously the wrapper still rendered `border-t … pt-1.5` around an
  // empty node, leaving the "random empty strip on the card below its email".
  const detailSlotContent = composeCardDetailContributions([
    {
      id: 'tenant-detail',
      visible: !isCompact,
      content: !isCompact ? detailsSlot?.(card) ?? null : null,
    },
  ])
  // On touch the rail and its picker fight for the same scarce screen: a
  // tablet picker opens large enough to sit over the rail that spawned it.
  // So while a picker is open we COLLAPSE the rail — but `hoverActive` above
  // deliberately stays true, because the gate at the render site unmounts this
  // whole subtree and the picker is portaled from inside it. Collapsing via
  // `visible` keeps the picker mounted; unmounting would blank it mid-choice.
  // A mouse keeps the rail up (pointer has to travel to the portaled menu, and
  // there is room for both) — pinned by KanbanCard.pickerHoverStack.pin.test.
  const touchRailMode = isCoarsePointer && !isPhone
  const hideActionBar = !isCardFaceBlockVisible(resolvedFace, 'actionBar')
  const hideCapture =
    !isCardFaceBlockVisible(resolvedFace, 'capture') || !extras.showCapture
  const recipeQuickActions = applyCardFaceRail(resolvedFace, quickActions)
  const strip = applyCardFaceStrip(resolvedFace, quickActions)
  const interactionControlProps = {
    api, card, currentUserId, onAddChecklistStep: handleAddChecklistStep,
    onChecklistChanged, onContextMenu, peekChecklistReady,
    quickActions: recipeQuickActions,
    resolveMember, threadSupport,
    visible: hoverActive && !(touchRailMode && quickPickerOpen),
    onPickerOpenChange: (open: boolean) => {
      setQuickPickerOpen(open)
      if (!open) setAllocateHotkey(false)
    },
    hideActionBar, hideCapture,
    showMoreKebab: railShows(resolvedFace.place, 'more'),
  }

  // W1-V03 (SAFETY-UI-LOCK-003) — access-locked card: the backend already
  // redacted every content field on this payload, so the normal face would
  // render an empty husk (and its hover peek / email actions would invite
  // fetches the viewer can't see). Render the padlock face on a simplified,
  // non-draggable tile instead. AFTER all hooks (rules of hooks — a lock
  // can appear/clear on the same mounted card between renders).
  if (card.access_locked) {
    return (
      <div
        data-card-id={card.id}
        data-card-surface={cardSurfaceId}
        data-card-runtime={cardSurfaceMode}
        data-access-locked="true"
        data-density={shownDensity}
        className={[
          'group relative rounded-xl border border-dashed border-amber-400/60 dark:border-amber-500/40',
          'bg-white dark:bg-zinc-800 select-none',
          isCompact ? 'py-1 pl-2 pr-2.5' : 'p-2 pr-2.5',
        ].join(' ')}
      >
        <KanbanCardLockedFace card={card} api={api} notify={notify} />
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={keyboardMode ? 0 : -1}
      draggable={draggable}
      aria-selected={onSelectToggle ? selected : undefined}
      data-selected={selected ? 'true' : undefined}
      {...rootHandlers}
      data-card-id={card.id}
      data-card-surface={cardSurfaceId}
      data-card-runtime={cardSurfaceMode}
      data-status={card.status}
      data-priority={card.priority}
      // FEAT-2026-05-28-KANBAN-CARD-PIN — surface pin state on the root so
      // tests / themes / future analytics can branch on it. Mirrors the
      // existing `data-priority` pattern.
      data-pinned={card.pinned ? 'true' : undefined}
      data-density={shownDensity}
      data-dragging={dragging || undefined}
      // Themed boards drive the card bg via `--card-bg`; falls through to the
      // zinc-800 default (className). Stage 2: the accent is a real `border` so
      // box-shadow is free for pure elevation + rings. Operator preference
      // (2026-06-29): the accent sits on the RIGHT edge — a colour tab clear of
      // the left-aligned title — so the root adds ~2px right padding (pr-2.5)
      // to keep content aligned.
      style={{
        // Per-card colour (Trello-style cover, lite) wins over the board theme's
        // `--card-bg`; falls back to it (then `transparent` → the className
        // surface) when the card has no colour.
        background: resolvedFace.fields.colorCover
          ? colorStyle.background ?? 'var(--card-bg, transparent)'
          : 'var(--card-bg, transparent)',
        // Themed boards expose `--card-fg`; falls through to currentColor on
        // unthemed boards (Tailwind .dark drives the title via the default
        // surface). Bug 2026-05-15 — themed boards used to render dark text on
        // a dark muted card_bg → unreadable.
        color: 'var(--card-fg, currentColor)',
        boxShadow: cardBoxShadow,
        borderRight: accentBorder,
        // Priority left-edge bar (High/Urgent only); undefined keeps the
        // themed 1px left border from the className.
        borderLeft: resolvedFace.fields.priority ? priorityBorder : undefined,
      }}
      className={[buildKanbanCardClassName({
        isCompact,
        isLarge,
        sensitive: Boolean(card.sensitive),
        snoozed: card.status === 'SNOOZED',
        hoverActive,
        dragging,
        emailDropHover,
        nestDropHover,
      }), tileHeight.frameClass].filter(Boolean).join(' ')}
    >
      <KanbanCardOverlays
        card={card}
        selected={selected}
        emailDropHover={emailDropHover}
        justAllocated={justAllocated}
        nestDropHover={nestDropHover}
      />
      {/* FEAT-2026-06-29-KANBAN-CARD-HOVER-MENU — right-edge hover menu. Revealed
          on hover; opens portaled panels into the lane gutter. Colour (labels +
          tile tint) and Draft (compose-from-card) are live; Calculator +
          Flowchart render disabled. The menu self-gates on capabilities, so a
          read-only / colour-unaware host (no `quickActions`) renders nothing. */}
      {allocateHotkey && recipeQuickActions && (recipeQuickActions.onAllocateToLane || recipeQuickActions.onAllocateToBoard) && (
        <KanbanCardAllocateHotkey
          card={card}
          handlers={recipeQuickActions}
          onClose={() => setAllocateHotkey(false)}
        />
      )}
      {hoverActive && quickActions && !quickPickerOpen && (
        <KanbanCardHoverMenu
          card={card}
          visible={hoverActive}
          onSetColor={strip.onSetColor}
          availableLabels={quickActions.availableLabels}
          labelRegistry={quickActions.labelRegistry}
          onAttachLabel={strip.onAttachLabel}
          onDetachLabel={strip.onDetachLabel}
          onComposeEmail={strip.onComposeEmail}
          members={quickActions.members}
          onOpenWorkbook={strip.onOpenWorkbook}
          onPrefetchWorkbook={strip.onPrefetchWorkbook}
          onOpenFlowchart={strip.onOpenFlowchart}
        />
      )}
      {!isCompact && api && isCardAiPending(card) && resolvedFace.fields.suggestedLane && (
        <CardAiBand
          allocationId={getCardAllocationId(card)}
          api={api}
          onChanged={onAiAllocationChanged}
          notify={notify}
        />
      )}
      <KanbanCardFace
        prefs={resolvedFace}
        slots={buildKanbanCardFaceSlots({
          card,
          resolvedFace,
          isCompact,
          isLarge,
          isPhone,
          isQuoteCard,
          hoverActive,
          hideActionBar,
          hideCapture,
          showDue,
          overdue,
          hovered,
          emailMeta,
          assignedMembers,
          checklistDone,
          checklistTotal,
          commentCount,
          relatedCount,
          unseenCount,
          checklistApiReady,
          checklistExpanded,
          setChecklistExpanded,
          onOpen,
          onRenameInline,
          onToggleComplete,
          highlightQuery,
          aiConfidencePct,
          lowConfidence,
          quickActions,
          onOpenEmail,
          onContextMenu,
          threadSupport,
          interactionControlProps,
          togglePhoneChrome,
          laneKind,
        })}
      />

      {checklistInlineReady && api && effectiveChecklistExpanded && (
        <KanbanCardInlineChecklist
          cardId={card.id}
          api={api}
          expanded={effectiveChecklistExpanded}
          density={density}
          allowCompact={hoverExpand || checklistForceOpen}
          onChanged={() => onChecklistChanged?.(card)}
          refreshToken={checklistRefreshTick}
        />
      )}

      <KanbanCardDetailSlot content={detailSlotContent} />
    </div>
  )
})
